import { useCallback, useEffect, useState } from "react";
import i18n from "@/i18n";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface Application {
  id: number;
  user_id: string | null;
  applicant_name: string;
  email: string;
  company: string | null;
  product_name: string;
  product_version: string;
  edition: string;
  seat_count: number;
  machine_code: string;
  use_case: string | null;
  status: string;
  reviewer_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  renew_license_id: number | null;
}

interface UseMyApplicationsResult {
  applications: Application[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useMyApplications(): UseMyApplicationsResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setApplications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from("license_applications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (queryError) throw queryError;
      setApplications((data as Application[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("apply.appsLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  return { applications, loading: loading || authLoading, error, reload: load };
}